import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { MapPin, Clock, Euro, ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation, Language } from '@/hooks/useTranslation';
import { OptimizedImage } from '@/components/OptimizedImage';

interface TourPlaceSectionProps {
  place: {
    name: string;
    city: string;
    country: string;
    image: string | null;
    description_fr: string | null;
    description_en: string | null;
    visit_tips_fr: string | null;
    visit_tips_en: string | null;
    latitude: number | null;
    longitude: number | null;
    site_officiel: string | null;
    prix: string | null;
    prix_en: string | null;
    heures: string | null;
  };
  index: number;
  language: Language;
}

const TourPlaceSection: React.FC<TourPlaceSectionProps> = ({ place, index, language }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const description = language === 'FR' ? place.description_fr : place.description_en;
  const visitTips = language === 'FR' ? place.visit_tips_fr : place.visit_tips_en;
  const pricing = language === 'FR' ? place.prix : place.prix_en;

  const handleDiscoverMore = () => {
    navigate(`/place/${encodeURIComponent(place.name)}`);
  };

  return (
    <section className="space-y-6">
      {/* Place Number and Title */}
      <div className="flex items-baseline gap-3">
        <Badge variant="secondary" className="text-lg font-bold px-4 py-2 rounded-full">
          {index}
        </Badge>
        <h2 className="text-2xl md:text-3xl font-bold text-foreground">
          {place.name}
        </h2>
      </div>

      {/* Place Image */}
      {place.image && (
        <div className="relative w-full aspect-[16/9] rounded-xl overflow-hidden shadow-lg">
          <OptimizedImage
            src={place.image}
            alt={place.name}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        </div>
      )}

      {/* Place Location */}
      <div className="flex items-center gap-2 text-muted-foreground">
        <MapPin className="w-4 h-4" />
        <span className="text-sm">
          {place.city}, {place.country}
        </span>
      </div>

      {/* About Section */}
      {description && (
        <Card className="border-none shadow-md">
          <CardContent className="pt-6">
            <h3 className="text-xl font-semibold mb-3 text-foreground">
              {t('aboutTitle')}
            </h3>
            <p className="text-base md:text-lg leading-relaxed text-muted-foreground whitespace-pre-wrap">
              {description}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Practical Information */}
      {(place.heures || pricing || place.site_officiel) && (
        <Card className="border-none shadow-md">
          <CardContent className="pt-6 space-y-3">
            <h3 className="text-xl font-semibold mb-3 text-foreground">
              {t('practicalInfoTitle')}
            </h3>
            
            {place.heures && (
              <div className="flex items-start gap-3">
                <Clock className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium text-foreground">{t('scheduleLabel')}</p>
                  <p className="text-sm text-muted-foreground">{place.heures}</p>
                </div>
              </div>
            )}

            {pricing && (
              <div className="flex items-start gap-3">
                <Euro className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium text-foreground">{t('pricingLabel')}</p>
                  <p className="text-sm text-muted-foreground">{pricing}</p>
                </div>
              </div>
            )}

            {place.site_officiel && (
              <div className="flex items-start gap-3">
                <ExternalLink className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium text-foreground">{t('siteLabel')}</p>
                  <a
                    href={place.site_officiel}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-primary hover:underline break-all"
                  >
                    {place.site_officiel}
                  </a>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Visit Tips Section */}
      {visitTips && (
        <Card className="border-none shadow-md bg-secondary/20">
          <CardContent className="pt-6">
            <h3 className="text-xl font-semibold mb-3 text-foreground">
              {t('visitTipsTitle')}
            </h3>
            <p className="text-base leading-relaxed text-muted-foreground whitespace-pre-wrap">
              {visitTips}
            </p>
          </CardContent>
        </Card>
      )}

      {/* CTA to discover more */}
      <div className="flex justify-center pt-2">
        <Button 
          onClick={handleDiscoverMore}
          variant="default"
          size="lg"
          className="w-full md:w-auto"
        >
          {t('tourPageDiscoverMore')} {place.name}
        </Button>
      </div>
    </section>
  );
};

export default TourPlaceSection;
